import {APIRegistry} from '@spothero/utils/api';
import isNumber from 'lodash/isNumber';

const TravelDistanceAPI = {
    /**
     * Retrieves walking or driving distances between origin and destination coordinates
     *
     * DISTANCE MATRIX
     * The Travel Distance API is sometimes referred to as the Distance Matrix.
     * While we don't have a need to pass multiple origins x multiple destinations, it is possible to do so.
     *
     * RETURNED DATA
     * Data from the API contains an array of objects, each containing request status, distance, and duration.
     * There are 2 strings that may come back under status: 'OK' and 'NO_DATA'.
     * Results come back in the same order that the request destinationParams were sent.
     *     [
     *         {status: 'OK', distance: 793, duration: 194},
     *         {status: 'NO_DATA', distance: null, duration: null}
     *     ]
     * @function getTravelDistances
     * @param {Object} params - The parameters to pass to the endpoint.
     * @param {Object} params.destination - An object containing a latitude and longitude
     * @param {Number} params.destination.latitude - The latitude of the destination
     * @param {Number} params.destination.longitude - The longitude of the destination
     * @param {Array} params.spotCoordinates - A list of spot ids and respective coordinates to start from
     * @param {String} params.spotCoordinates[].spotId - Spot Id
     * @param {Number} params.spotCoordinates[].latitude - Spot latitude
     * @param {Number} params.spotCoordinates[].longitude - Spot longitude
     * @param {Boolean} params.isDriving - Whether to query based on driving or walking directions. Defaults to false.
     * @example
     * getTravelDistances({
     *  origin: {latitude: 41.8781, longitude: -87.6298},
     *  spotCoordinates: [{spotId: 2, latitude: 41.8881, longitude: -87.6298}]
     *  isDriving: true
     * });
     * @returns {Array} A list of spot ids and respective distances to destination
     */
    getTravelDistances: ({destination, spotCoordinates, isDriving = false}) => {
        const travelMode = isDriving ? 'DRIVING' : 'WALKING';
        const destinationParams = `${destination.latitude},${destination.longitude}`;
        const originParams = spotCoordinates
            .map(spot => `&origins=${spot.latitude},${spot.longitude}`)
            .join('');

        return new Promise((resolve, reject) => {
            APIRegistry.get('services')
                .get(
                    `/distance/v1/distance/?unitSystem=IMPERIAL&travelMode=${travelMode}destinations=${destinationParams}&${originParams}`
                )
                .then(({data: {data}}) => {
                    // transform the data and return it
                    const travelDistanceResults = spotCoordinates.map(
                        (spot, index) => {
                            const result = data[index];

                            if (!result) {
                                return {
                                    spotId: spot.spotId,
                                };
                            }
                            const {status, distance, duration} = result;

                            const successful =
                                status !== 'NO_DATA' &&
                                isNumber(distance) &&
                                isNumber(duration);
                            const distanceInMiles = successful
                                ? distance / 5280
                                : null;

                            return {
                                spotId: spot.spotId,
                                ...(successful &&
                                    (isDriving
                                        ? {
                                              drivingDistanceInMiles: distanceInMiles,
                                              drivingDurationInSeconds: duration,
                                          }
                                        : {
                                              walkingDistanceInMiles: distanceInMiles,
                                              walkingDurationInSeconds: duration,
                                          })),
                            };
                        }
                    );

                    resolve(travelDistanceResults);
                })
                .catch(response => {
                    // eslint-disable-next-line no-console
                    console.log(
                        `No results returned from the Distance Matrix Service.`
                    );
                    // Errors or unsuccessful calls are fine; just return an empty array with spotIds
                    const emptyResults = spotCoordinates.map(spot => {
                        return {spotId: spot.spotId};
                    });

                    resolve(emptyResults);
                });
        });
    },
};

export default TravelDistanceAPI;
